import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import { resolvePublicFileUrl } from '../../utils/imageUrls';
import { vehicleProfilePath, resolveVehicleNavSlug } from '../../utils/vehicleUrls';
import apiService from '../../services/ApiService';
import type { FeaturedBuild } from './homeTypes';

interface FeaturedBuildCardProps {
  featured: FeaturedBuild;
  compact?: boolean;
}

export const FeaturedBuildCard: React.FC<FeaturedBuildCardProps> = ({ featured, compact = false }) => {
  const navigate = useNavigate();
  const v = featured.vehicle;
  if (!v) return null;

  const slug = resolveVehicleNavSlug(v);
  const href =
    v.owner_username && slug ? vehicleProfilePath(v.owner_username, slug) : '';
  const imageUrl = resolvePublicFileUrl(v.image_url);
  const title = v.nickname || v.display_name || `${v.year} ${v.make} ${v.model}`;
  const subtitle = v.nickname
    ? `${v.year} ${v.make} ${v.model}${v.trim ? ` ${v.trim}` : ''}`
    : undefined;

  const open = () => {
    if (!href) return;
    void apiService.trackEvent('home_featured_clicked', {
      vehicle_id: v.id,
      metadata: { compact },
    });
    navigate(href);
  };

  if (compact) {
    return (
      <article className="flex gap-4 items-stretch">
        <button
          type="button"
          onClick={open}
          className="relative w-36 sm:w-48 aspect-[4/3] flex-shrink-0 overflow-hidden rounded-lg bg-[#0a0a0a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
          aria-label={`View featured build ${title}`}
        >
          {imageUrl ? (
            <img src={imageUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-600 text-sm">No photo</div>
          )}
        </button>
        <div className="min-w-0 flex flex-col justify-center py-1">
          <p className="text-meta mb-1">Featured build</p>
          <h3 className="text-white font-semibold text-lg leading-tight truncate">{title}</h3>
          {subtitle ? <p className="text-gray-400 text-sm mt-1 truncate">{subtitle}</p> : null}
          {featured.blurb ? (
            <p className="text-gray-500 text-sm mt-2 line-clamp-2">{featured.blurb}</p>
          ) : null}
          <div className="mt-3">
            <Button type="button" variant="secondary" size="sm" onClick={open}>
              View build
            </Button>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
      <button
        type="button"
        onClick={open}
        className="group relative w-full aspect-[16/10] overflow-hidden rounded-2xl bg-[#0a0a0a] ring-1 ring-white/5 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
        aria-label={`View featured build ${title}`}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover motion-safe:transition-transform motion-safe:duration-500 motion-safe:group-hover:scale-[1.02]"
            loading="eager"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600">No photo</div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0a0a0a]/50 to-transparent" aria-hidden />
      </button>
      <div className="min-w-0">
        <p className="eyebrow mb-2.5">Featured build</p>
        <h3 className="heading-section text-2xl sm:text-3xl mb-2">{title}</h3>
        {subtitle ? <p className="font-mono text-sm text-gray-400 mb-3">{subtitle}</p> : null}
        {featured.blurb ? (
          <p className="text-gray-300 leading-relaxed mb-6 max-w-lg">{featured.blurb}</p>
        ) : featured.curator_label ? (
          <p className="text-gray-400 leading-relaxed mb-6 max-w-lg">{featured.curator_label}</p>
        ) : (
          <p className="text-gray-400 leading-relaxed mb-6 max-w-lg">
            Mods, photos, and history on one public profile.
          </p>
        )}
        <Button type="button" variant="primary" onClick={open}>
          View build
        </Button>
      </div>
    </article>
  );
};

export default FeaturedBuildCard;
